import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Trash2, Copy, KeyRound, Users as UsersIcon, Building2, Plug, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatNumber, cn } from "@/lib/utils";
import { useMe } from "@/hooks/use-me";
import { useTeam, useInviteUser, useRemoveTeamMember, usePatchTeamMember } from "@/hooks/use-team";
import {
  useAccountApiKeys, useCreateAccountApiKey, useRevokeAccountApiKey, useDeleteAccountApiKey,
} from "@/hooks/use-account-api-keys";
import { useProfiles, useConnectProfile, useDisconnectProfile } from "@/hooks/use-profiles";

export const Route = createFileRoute("/_auth/settings")({
  component: SettingsPage,
});

type Tab = "account" | "team" | "profiles" | "apikeys";

function SettingsPage() {
  const [tab, setTab] = useState<Tab>("account");
  const { data: me } = useMe();
  const canEdit = me?.role === "owner" || me?.role === "admin";

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Configuración</h1>
        <p className="text-sm text-ink-500 mt-1">Tu cuenta, equipo, perfiles conectados y API keys.</p>
      </header>

      <div className="flex gap-1 border-b border-ink-200 dark:border-ink-800 mb-4 overflow-x-auto">
        {([
          ["account", "Cuenta", Building2],
          ["team", "Equipo", UsersIcon],
          ["profiles", "Perfiles", Plug],
          ["apikeys", "API Keys", KeyRound],
        ] as Array<[Tab, string, typeof Building2]>).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              "h-10 px-4 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2",
              tab === key
                ? "border-brand-600 text-brand-700 dark:text-brand-300"
                : "border-transparent text-ink-500 hover:text-ink-700",
            )}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "account" && <AccountTab canEdit={canEdit} />}
      {tab === "team" && <TeamTab canEdit={canEdit} currentUserId={me?.id ?? null} />}
      {tab === "profiles" && <ProfilesTab canEdit={canEdit} />}
      {tab === "apikeys" && <ApiKeysTab canEdit={canEdit} />}
    </div>
  );
}

// ============ Account ============

const accountSchema = z.object({
  name: z.string().min(2),
  tz: z.string().min(2),
});
type AccountInput = z.infer<typeof accountSchema>;

function AccountTab({ canEdit }: { canEdit: boolean }) {
  const account = useQuery({ queryKey: ["account"], queryFn: () => api.getAccount() });
  const usage = useQuery({ queryKey: ["usage"], queryFn: () => api.getUsage() });

  const form = useForm<AccountInput>({
    resolver: zodResolver(accountSchema),
    values: account.data ? { name: account.data.name, tz: account.data.tz } : undefined,
  });

  async function onSubmit(data: AccountInput) {
    try {
      await api.patchAccount(data);
      account.refetch();
      toast.success("Cuenta actualizada");
    } catch {}
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Información de la cuenta</CardTitle>
        </CardHeader>
        <CardContent>
          {!account.data ? (
            <Skeleton className="h-32" />
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input className="mt-1" disabled={!canEdit} {...form.register("name")} />
              </div>
              <div>
                <Label>Zona horaria</Label>
                <Input className="mt-1" disabled={!canEdit} {...form.register("tz")} />
              </div>
              <div className="text-xs text-ink-500 space-y-1">
                <div>Tipo: <Badge variant="muted">{account.data.type}</Badge></div>
                <div>Plan: <Badge>{account.data.plan?.name ?? "Sin plan"}</Badge></div>
                <div>Estado: <Badge variant={account.data.status === "active" ? "good" : "bad"}>{account.data.status}</Badge></div>
              </div>
              {canEdit && <Button type="submit">Guardar cambios</Button>}
            </form>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Consumo del mes</CardTitle>
          <CardDescription>{usage.data?.month}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!usage.data ? (
            <Skeleton className="h-32" />
          ) : (
            <>
              <UsageBar label="Menciones" used={usage.data.mentions.consumed} quota={usage.data.mentions.quota} percent={usage.data.mentions.percent} />
              <UsageBar label="Proyectos" used={usage.data.projects.used} quota={usage.data.projects.quota} percent={(usage.data.projects.used / Math.max(usage.data.projects.quota, 1)) * 100} />
              <UsageBar label="Perfiles analíticos" used={usage.data.profiles_analytic.used} quota={usage.data.profiles_analytic.quota} percent={(usage.data.profiles_analytic.used / Math.max(usage.data.profiles_analytic.quota, 1)) * 100} />
              <UsageBar label="Perfiles CRM" used={usage.data.profiles_crm.used} quota={usage.data.profiles_crm.quota} percent={(usage.data.profiles_crm.used / Math.max(usage.data.profiles_crm.quota, 1)) * 100} />
              <UsageBar label="Usuarios" used={usage.data.users.used} quota={usage.data.users.quota} percent={(usage.data.users.used / Math.max(usage.data.users.quota, 1)) * 100} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsageBar({ label, used, quota, percent }: { label: string; used: number; quota: number; percent: number }) {
  const tone = percent >= 90 ? "bg-bad" : percent >= 70 ? "bg-warn" : "bg-good";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-ink-700 dark:text-ink-300">{label}</span>
        <span className="text-ink-500 font-mono">{formatNumber(used)} / {formatNumber(quota)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  );
}

// ============ Team ============

const inviteSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(8),
  role: z.enum(["owner", "admin", "editor", "analyst", "responder", "viewer"]),
});
type InviteInput = z.infer<typeof inviteSchema>;

function TeamTab({ canEdit, currentUserId }: { canEdit: boolean; currentUserId: number | null }) {
  const team = useTeam();
  const remove = useRemoveTeamMember();
  const patchUser = usePatchTeamMember();
  const [showModal, setShowModal] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Miembros del equipo</CardTitle>
          <CardDescription>{team.data?.length ?? 0} usuarios en tu cuenta</CardDescription>
        </div>
        {canEdit && (
          <Button onClick={() => setShowModal(true)}>
            <Plus size={14} /> Invitar usuario
          </Button>
        )}
      </CardHeader>
      <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
        {team.isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)
        ) : !team.data || team.data.length === 0 ? (
          <p className="text-sm text-ink-500 py-6 text-center">Sin miembros aún.</p>
        ) : (
          team.data.map((u) => (
            <div key={u.id} className="py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-bold flex items-center justify-center">
                {u.name.slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-900 dark:text-white">{u.name}</span>
                  {u.id === currentUserId && <Badge variant="muted">tú</Badge>}
                </div>
                <p className="text-xs text-ink-500">{u.email}</p>
              </div>
              {canEdit && u.id !== currentUserId ? (
                <select
                  value={u.role}
                  onChange={(e) => patchUser.mutate({ id: u.id, body: { role: e.target.value } })}
                  className="h-8 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 text-xs"
                >
                  {["owner", "admin", "editor", "analyst", "responder", "viewer"].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              ) : (
                <Badge variant="muted">{u.role}</Badge>
              )}
              {canEdit && u.id !== currentUserId && (
                <button
                  onClick={() => { if (confirm(`¿Eliminar a ${u.email}?`)) remove.mutate(u.id); }}
                  className="text-ink-400 hover:text-bad p-2"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))
        )}
      </CardContent>
      {showModal && <InviteUserModal onClose={() => setShowModal(false)} />}
    </Card>
  );
}

function InviteUserModal({ onClose }: { onClose: () => void }) {
  const invite = useInviteUser();
  const form = useForm<InviteInput>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { role: "viewer" },
  });

  async function onSubmit(data: InviteInput) {
    try {
      await invite.mutateAsync(data);
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Invitar usuario</CardTitle>
          <CardDescription>Crea cuenta para un miembro del equipo.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" {...form.register("name")} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" className="mt-1" {...form.register("email")} />
              {form.formState.errors.email && <p className="text-xs text-bad mt-1">{form.formState.errors.email.message}</p>}
            </div>
            <div>
              <Label>Contraseña inicial</Label>
              <Input type="password" className="mt-1" {...form.register("password")} />
              {form.formState.errors.password && <p className="text-xs text-bad mt-1">{form.formState.errors.password.message}</p>}
            </div>
            <div>
              <Label>Rol</Label>
              <select className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm" {...form.register("role")}>
                <option value="owner">owner (control total)</option>
                <option value="admin">admin (sin facturación)</option>
                <option value="editor">editor (proyectos + plantillas)</option>
                <option value="analyst">analyst (reportes)</option>
                <option value="responder">responder (solo inbox)</option>
                <option value="viewer">viewer (solo lectura)</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? "Invitando…" : "Invitar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Profiles ============

const NETWORKS = [
  { id: "x", label: "X / Twitter" },
  { id: "ig", label: "Instagram" },
  { id: "fb", label: "Facebook" },
  { id: "li", label: "LinkedIn" },
  { id: "yt", label: "YouTube" },
  { id: "tt", label: "TikTok" },
  { id: "reddit", label: "Reddit" },
  { id: "google", label: "Google Business" },
  { id: "trustpilot", label: "Trustpilot" },
];

const profileSchema = z.object({
  network: z.string().min(1),
  kind: z.enum(["analytic", "crm"]),
  handle: z.string().min(1),
  display_name: z.string().optional(),
});
type ProfileInput = z.infer<typeof profileSchema>;

const PROVIDER_LABEL: Record<string, string> = {
  x: "X (Twitter)",
  meta: "Meta (Facebook + Instagram)",
  google: "Google / YouTube",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  tiktok: "TikTok",
};

function OAuthConnectGrid({ canEdit }: { canEdit: boolean }) {
  const providers = useQuery({
    queryKey: ["oauth", "providers"],
    queryFn: () => api.oauth.listProviders(),
    staleTime: 5 * 60_000,
  });
  const qc = useQueryClient();

  // Detectar retorno desde callback OAuth.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("oauth") === "ok") {
      toast.success(`Cuenta ${params.get("network") ?? ""} conectada`);
      qc.invalidateQueries({ queryKey: ["profiles"] });
      // limpiar query string
      const url = new URL(window.location.href);
      url.searchParams.delete("oauth");
      url.searchParams.delete("network");
      window.history.replaceState({}, "", url.toString());
    }
  }, [qc]);

  async function connect(provider: string) {
    try {
      const { authorize_url } = await api.oauth.start(provider);
      window.location.href = authorize_url;
    } catch {
      // toast lo muestra http.ts
    }
  }

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Conectar con OAuth</CardTitle>
        <CardDescription>
          Conecta cuentas reales de cada plataforma. Los workers usarán estos tokens para ingestar.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {providers.isLoading ? (
          <Skeleton className="h-24" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(providers.data ?? []).map((p) => (
              <div
                key={p.provider}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-ink-200 dark:border-ink-700"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{PROVIDER_LABEL[p.provider] ?? p.provider}</p>
                  <p className="text-[11px] text-ink-500">red: {p.network}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canEdit || !p.configured}
                  onClick={() => connect(p.provider)}
                  title={!p.configured ? "Falta config OAuth en .env" : undefined}
                >
                  <ExternalLink size={12} />
                  {p.configured ? "Conectar" : "no config"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProfilesTab({ canEdit }: { canEdit: boolean }) {
  const profiles = useProfiles();
  const disconnect = useDisconnectProfile();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <OAuthConnectGrid canEdit={canEdit} />
      <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Perfiles conectados</CardTitle>
          <CardDescription>Redes sociales que la plataforma monitorea o gestiona</CardDescription>
        </div>
        {canEdit && (
          <Button onClick={() => setShowModal(true)}>
            <Plus size={14} /> Manual
          </Button>
        )}
      </CardHeader>
      <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
        {profiles.isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)
        ) : !profiles.data || profiles.data.length === 0 ? (
          <p className="text-sm text-ink-500 py-6 text-center">Sin perfiles conectados.</p>
        ) : (
          profiles.data.map((p) => (
            <div key={p.id} className="py-3 flex items-center gap-3">
              <Plug size={18} className="text-ink-400" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-900 dark:text-white">{p.handle}</span>
                  <Badge variant="muted">{p.network}</Badge>
                  <Badge variant={p.kind === "crm" ? "default" : "muted"}>{p.kind}</Badge>
                </div>
                <p className="text-xs text-ink-500">
                  Estado: <span className={p.status === "connected" ? "text-good" : "text-warn"}>{p.status}</span>
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => { if (confirm(`¿Desconectar ${p.handle}?`)) disconnect.mutate(p.id); }}
                  className="text-ink-400 hover:text-bad p-2"
                  title="Desconectar"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))
        )}
      </CardContent>
      {showModal && <ConnectProfileModal onClose={() => setShowModal(false)} />}
      </Card>
    </>
  );
}

function ConnectProfileModal({ onClose }: { onClose: () => void }) {
  const connect = useConnectProfile();
  const form = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: { kind: "analytic", network: "x" },
  });

  async function onSubmit(data: ProfileInput) {
    try {
      await connect.mutateAsync(data);
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Conectar perfil manual</CardTitle>
          <CardDescription>Alta manual para redes sin OAuth nativo (WhatsApp, Messenger, Trustpilot, etc.).</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Red social</Label>
              <select className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm" {...form.register("network")}>
                {NETWORKS.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Tipo</Label>
              <select className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm" {...form.register("kind")}>
                <option value="analytic">Analytic (solo monitoreo)</option>
                <option value="crm">CRM (responder mensajes)</option>
              </select>
            </div>
            <div>
              <Label>Handle / cuenta</Label>
              <Input className="mt-1" placeholder="@mi_marca" {...form.register("handle")} />
            </div>
            <div>
              <Label>Nombre visible (opcional)</Label>
              <Input className="mt-1" {...form.register("display_name")} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={connect.isPending}>
                {connect.isPending ? "Conectando…" : "Conectar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ API Keys ============

function ApiKeysTab({ canEdit }: { canEdit: boolean }) {
  const keys = useAccountApiKeys();
  const revoke = useRevokeAccountApiKey();
  const del = useDeleteAccountApiKey();
  const [showModal, setShowModal] = useState(false);
  const [created, setCreated] = useState<{ name: string; plaintext: string } | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>Tokens para acceder a la API de tu cuenta desde sistemas externos</CardDescription>
        </div>
        {canEdit && (
          <Button onClick={() => setShowModal(true)}>
            <Plus size={14} /> Generar key
          </Button>
        )}
      </CardHeader>
      <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
        {keys.isLoading ? (
          Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)
        ) : !keys.data || keys.data.length === 0 ? (
          <p className="text-sm text-ink-500 py-6 text-center">Sin API keys generadas.</p>
        ) : (
          keys.data.map((k) => (
            <div key={k.id} className="py-3 flex items-center gap-3">
              <KeyRound size={18} className="text-ink-400" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-900 dark:text-white">{k.name}</span>
                  {k.revoked_at && <Badge variant="bad">revocada</Badge>}
                </div>
                <p className="text-xs font-mono text-ink-500">
                  {k.prefix}••• · creada {new Date(k.created_at).toLocaleString("es-PE")}
                  {k.last_used_at && ` · último uso ${new Date(k.last_used_at).toLocaleString("es-PE")}`}
                </p>
              </div>
              {canEdit && !k.revoked_at && (
                <button onClick={() => revoke.mutate(k.id)} className="text-warn hover:text-warn/80 text-xs font-medium px-2">
                  Revocar
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => { if (confirm(`¿Eliminar permanentemente ${k.name}?`)) del.mutate(k.id); }}
                  className="text-ink-400 hover:text-bad p-2"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))
        )}
      </CardContent>
      {showModal && (
        <NewKeyModal
          onClose={() => setShowModal(false)}
          onCreated={(name, plaintext) => {
            setShowModal(false);
            setCreated({ name, plaintext });
          }}
        />
      )}
      {created && <CreatedKeyDialog name={created.name} plaintext={created.plaintext} onClose={() => setCreated(null)} />}
    </Card>
  );
}

function NewKeyModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: (name: string, plaintext: string) => void }) {
  const create = useCreateAccountApiKey();
  const [name, setName] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const res = await create.mutateAsync({ name });
      onCreated(name, res.plaintext);
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Generar API key</CardTitle>
          <CardDescription>El token plaintext se mostrará UNA sola vez.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" placeholder="ej. CI pipeline" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending || !name.trim()}>
                {create.isPending ? "Generando…" : "Generar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function CreatedKeyDialog({ name, plaintext, onClose }: { name: string; plaintext: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>API key "{name}" generada</CardTitle>
          <CardDescription className="text-warn">
            ⚠️ Cópiala ahora. No la podrás ver de nuevo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="font-mono text-sm bg-ink-100 dark:bg-ink-800 p-3 rounded-md break-all">
            {plaintext}
          </div>
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(plaintext);
              toast.success("Copiado al portapapeles");
            }}
            className="w-full"
          >
            <Copy size={14} /> Copiar
          </Button>
          <Button onClick={onClose} className="w-full">Listo</Button>
        </CardContent>
      </Card>
    </div>
  );
}
